import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Badge,
  Menu as MuiMenu,
  MenuItem,
  Button,
  Divider,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Notifications as NotificationsIcon, DoneAll } from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/auth/AuthContext';
import { useWebSocket } from '../../services/websocket/WebSocketProvider';
import { closeSnackbar, enqueueSnackbar } from "notistack";
import NotificationService from '../../services/api/notifications';

const Notifications = () => {
  const { user, setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // State for notifications dropdown
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const isNotificationsOpen = Boolean(notificationsAnchorEl);

  // Fetch unread notifications from API
  const fetchUnreadNotifications = async () => {
    if (!user?.role?.includes('support')) return;
    
    try {
      setLoading(true);
      const response = await NotificationService.getUnreadNotifications(
        setIsAuthenticated,
        navigate
      );

      if (response.success) {
        // Directly use API data
        const api_data = response.notifications;
        setNotifications(api_data);
        setUnreadCount(response.count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      const response = await NotificationService.markNotificationAsRead(
        notificationId,
        setIsAuthenticated,
        navigate
      );

      if (response.success) {
        // Remove from notifications list and update count
        setNotifications(prev => prev.filter(n => (n.id || n.additional_data?.notification_id) !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = async () => {
    try {
      setMarkingAllRead(true);
      
      // Get all notifications that have id or notification_id
      const notificationsWithId = notifications.filter(n => n.id || n.additional_data?.notification_id);
      
      // Mark all notifications as read via API (pass null to mark all)
      if (notificationsWithId.length > 0) {
        const response = await NotificationService.markNotificationAsRead(
          null, // Pass null to mark all notifications as read
          setIsAuthenticated,
          navigate
        );
        
        if (!response.success) {
          throw new Error('Failed to mark notifications as read');
        }
      }
      
      // Clear all notifications from local state and reset count
      setNotifications([]);
      setUnreadCount(0);
      
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      
      // Fallback: clear local state for notifications without notification_id
      const notificationsWithoutId = notifications.filter(n => !n.id && !n.additional_data?.notification_id);
      
      if (notificationsWithoutId.length > 0) {
        setNotifications(prev => prev.filter(n => n.id || n.additional_data?.notification_id));
        setUnreadCount(prev => Math.max(0, prev - notificationsWithoutId.length));
      }
    } finally {
      setMarkingAllRead(false);
    }
  };

  // Initialize notifications on component mount
  useEffect(() => {
    if (user?.role?.includes('support')) {
      fetchUnreadNotifications();
    }
  }, [user]);

  // Handle opening notifications dropdown
  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  
  // Handle closing notifications dropdown
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  // WebSocket integration for live notifications
  useWebSocket({
    userId: user?.id,
    onMessage: (message) => {
      if (!user) return;
     
      
      // Handle live notifications
      if (message.notification_type === 'callback' || message.notification_type === 'enquiry') {
        // Append WebSocket data to existing notifications
        setNotifications(prev => [message, ...prev]);
        setUnreadCount(prev => prev + 1);
      }

      enqueueSnackbar(
        `New notification: ${message.message || 'New Notification'}`,
         {
                                variant: "info",
                                autoHideDuration: 5000,
                                action: (messageId) => (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            navigate(message.additional_data.action_url);
                                            closeSnackbar(messageId);
                                        }}
                                        sx={{ color: 'white' }}
                                    >
                                        <OpenInNew />
                                    </IconButton>
                                ),
                            }
      )
    }
  });

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    handleNotificationsClose();
    
    // Mark as read if notification has id or notification_id
    const notificationId = notification.id || notification.additional_data?.notification_id;
    if (notificationId) {
      await markNotificationAsRead(notificationId);
    } else {
      // For notifications without id, just remove from local state
      setNotifications(prev => prev.filter(n => n !== notification));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // Get action URL from either API structure or WebSocket structure
    const actionUrl = notification.action_url || notification.additional_data?.action_url;
    
    if (actionUrl) {
      navigate(actionUrl);
    } else {
      navigate('/callbacks');
    }
  };

  // Format notification display data consistently
  const formatNotificationData = (notification) => {
    return {
      // Handle both API and WebSocket data structures
      id: notification.id || notification.additional_data?.notification_id ,
      title: notification.title || 'New Notification',
      message: notification.message || 'New notification',
      timestamp: notification.created_at,
      badge: notification.category || notification.notification_type || notification.priority || notification.source || notification.sender_type || 'Info'
    };
  };

  // Check if there are any notifications to show Mark All Read button
  const hasAnyNotifications = notifications.length > 0;

  // Don't render if user is not support
  if (!user?.role?.includes('support')) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label="notifications"
        aria-controls={isNotificationsOpen ? 'notifications-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isNotificationsOpen ? 'true' : undefined}
        onClick={handleNotificationsClick}
        sx={{ color: 'text.primary' }}
      >
        <Badge badgeContent={unreadCount} color="secondary">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      
      {/* Notifications Dropdown Menu */}
      <MuiMenu
        id="notifications-menu"
        anchorEl={notificationsAnchorEl}
        open={isNotificationsOpen}
        onClose={handleNotificationsClose}
        PaperProps={{
          elevation: 0,
          sx: {
            width: 360,
            maxHeight: 500,
            overflow: 'hidden',
            borderRadius: 3,
            mt: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }
        }}
        MenuListProps={{
          'aria-labelledby': 'notifications-button',
          sx: { p: 0 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 2, 
          px: 3,
          fontWeight: 'bold',
          fontSize: '1.1rem',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="secondary" />
          )}
        </Box>

        {/* Mark All Read Button */}
        {hasAnyNotifications && (
          <>
            <Box sx={{ p: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DoneAll />}
                onClick={markAllNotificationsAsRead}
                disabled={markingAllRead}
                fullWidth
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    bgcolor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {markingAllRead ? 'Marking All Read...' : 'Mark All Read'}
              </Button>
            </Box>
            <Divider />
          </>
        )}
        
        {/* Notifications List */}
        <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ 
              py: 4, 
              px: 3, 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              Loading notifications...
            </Box>
          ) : notifications?.length > 0 ? (
            notifications.map((notification, index) => {
              // {api_data || ws_data}
              const formattedNotification = formatNotificationData(notification);
              
              return (
                <MenuItem 
                  key={formattedNotification.id || index} 
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 2,
                    px: 3,
                    borderBottom: index < notifications.length - 1 ? '1px solid' : 'none',
                    borderColor: 'rgba(0, 0, 0, 0.06)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    },
                    minHeight: 'auto',
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    {/* Title */}
                    <Box sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      lineHeight: 1.3,
                      mb: 0.5,
                      color: 'text.primary',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere'
                    }}>
                      {formattedNotification.title}
                    </Box>
                    
                    {/* Message */}
                    <Box sx={{ 
                      fontWeight: 'medium',
                      fontSize: '0.85rem',
                      lineHeight: 1.4,
                      mb: 1,
                      color: 'text.secondary',
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal'
                    }}>
                      {formattedNotification.message}
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1
                    }}>
                      {/* Timestamp */}
                      {formattedNotification.timestamp && (
                        <Box sx={{ 
                          fontSize: '0.7rem', 
                          color: 'text.secondary',
                          fontWeight: 400,
                          wordWrap: 'break-word',
                          flex: '1 1 auto',
                          minWidth: 0
                        }}>
                          {new Date(formattedNotification.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Box>
                      )}
                      
                      {/* Badge */}
                      <Box sx={{ 
                        fontSize: '0.65rem', 
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        bgcolor: theme.palette.primary.main + '15',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        textTransform: 'capitalize',
                        flexShrink: 0,
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'nowrap'
                      }}>
                        {formattedNotification.badge}
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })
          ) : (
            <Box sx={{ 
              py: 6, 
              px: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              color: 'text.secondary',
              textAlign: 'center',
              minHeight: 200,
              justifyContent: 'center'
            }}>
              <NotificationsIcon sx={{ 
                fontSize: '3rem', 
                opacity: 0.3, 
                mb: 2,
                color: 'text.disabled'
              }} />
              <Box sx={{ 
                fontSize: '1rem',
                fontWeight: 500,
                mb: 0.5,
                color: 'text.primary'
              }}>
                No notifications
              </Box>
              <Box sx={{ 
                fontSize: '0.85rem',
                opacity: 0.7,
                color: 'text.secondary'
              }}>
                You're all caught up!
              </Box>
            </Box>
          )}
        </Box>
      </MuiMenu>
    </>
  );
};

export default Notifications;